// src/pages/PendingReview.tsx
// Shown when a user's account_status is 'pending_review' or 'rejected' or 'suspended'.
// They cannot access the main app until their status changes.

import React from 'react';
import { useAuth } from '../context/AuthContext';
import { useProfile } from '../hooks/useProfile';

export default function PendingReview() {
  const { signOut } = useAuth();
  const { profile, firm } = useProfile();

  if (!profile) return null;

  const isPending = profile.account_status === 'pending_review';
  const isRejected = profile.account_status === 'rejected';
  const isSuspended = profile.account_status === 'suspended';

  const status = isPending ? 'Pending Review' : isRejected ? 'Application Declined' : 'Account Suspended';
  const statusColor = isPending ? '#92640A' : isRejected ? '#C0222A' : '#C0222A';

  return (
    <div style={{
      minHeight: '100vh',
      background: '#F5F6F8',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 24,
    }}>
      <div style={{
        maxWidth: 520,
        width: '100%',
        background: '#FFFFFF',
        border: '1px solid #D0D5DD',
        borderRadius: 4,
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        padding: 40,
      }}>
        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 32 }}>
          <svg width="32" height="32" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M 4 14 A 10 10 0 0 1 24 14" stroke="#0A1628" strokeWidth="2.5" strokeLinecap="round"/>
            <path d="M 6 14 A 8 8 0 0 0 22 14" stroke="#C9A84C" strokeWidth="2.5" strokeLinecap="round"/>
            <circle cx="14" cy="14" r="2.5" fill="#C9A84C"/>
          </svg>
          <span style={{ fontFamily: 'Georgia, serif', fontSize: 22, color: '#0A0A0A' }}>
            Altveno
          </span>
        </div>

        {/* Status badge */}
        <div style={{
          display: 'inline-block',
          padding: '4px 10px',
          borderRadius: 4,
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: 0.5,
          textTransform: 'uppercase',
          backgroundColor: `${statusColor}15`,
          color: statusColor,
          marginBottom: 20,
        }}>
          {status}
        </div>

        {/* Headline */}
        <div style={{ fontSize: 22, fontWeight: 700, color: '#0A0A0A', marginBottom: 12 }}>
          {isPending && 'Your application is being reviewed.'}
          {isRejected && 'Your application was not approved.'}
          {isSuspended && 'Your account has been suspended.'}
        </div>

        {/* Body */}
        <div style={{ fontSize: 14, color: '#6B7280', lineHeight: 1.6, marginBottom: 28 }}>
          {isPending && (
            <>
              Thank you for signing up to Altveno. Our team is reviewing your application to verify your firm's
              eligibility. This typically takes 1–2 business days.
              <br /><br />
              You'll receive an email at <strong style={{ color: '#0A0A0A' }}>{profile.email}</strong> as soon as your
              account is approved.
            </>
          )}
          {isRejected && (
            <>
              We've reviewed your application and are unable to approve it at this time.
              {profile.rejection_reason && (
                <>
                  <br /><br />
                  <strong style={{ color: '#0A0A0A' }}>Reason:</strong> {profile.rejection_reason}
                </>
              )}
              <br /><br />
              If you believe this is an error or would like to provide additional information, please contact us at{' '}
              <a href="mailto:support@altveno.com" style={{ color: '#0D6E8A' }}>support@altveno.com</a>.
            </>
          )}
          {isSuspended && (
            <>
              Your account access has been suspended. Please contact us at{' '}
              <a href="mailto:support@altveno.com" style={{ color: '#0D6E8A' }}>support@altveno.com</a> for more
              information.
            </>
          )}
        </div>

        {/* Submission summary */}
        {isPending && (
          <div style={{
            backgroundColor: '#F5F6F8',
            border: '1px solid #D0D5DD',
            borderRadius: 4,
            padding: 16,
            marginBottom: 28,
          }}>
            <div style={{ fontSize: 11, color: '#6B7280', letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 8 }}>
              Your Submission
            </div>
            <div style={{ fontSize: 13, color: '#0A0A0A', lineHeight: 1.7 }}>
              <div><strong>Name:</strong> {profile.full_name}</div>
              <div><strong>Email:</strong> {profile.email}</div>
              <div><strong>Role:</strong> {profile.role === 'manager' ? 'Manager' : 'Allocator'}</div>
              {firm && (
                <>
                  <div><strong>Firm:</strong> {firm.name}</div>
                  <div><strong>Type:</strong> {firm.type}</div>
                  <div><strong>Location:</strong> {firm.headquarters}</div>
                </>
              )}
              {profile.submitted_at && (
                <div style={{ marginTop: 8, color: '#6B7280', fontSize: 12 }}>
                  Submitted {new Date(profile.submitted_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                </div>
              )}
            </div>
          </div>
        )}

        <button
          onClick={signOut}
          style={{
            background: '#FFFFFF',
            border: '1px solid #D0D5DD',
            color: '#3D4552',
            padding: '10px 20px',
            borderRadius: 3,
            fontSize: 13,
            cursor: 'pointer',
            fontFamily: 'system-ui, -apple-system, sans-serif',
          }}
        >
          Sign out
        </button>
      </div>
    </div>
  );
}